import { Header } from "@/components/SiteUI"
import posts from "@/shared/api/posts";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { Essentials, Paragraph, Bold, Italic, Heading, Autoformat, BlockQuote, CloudServices, Base64UploadAdapter, Indent, Link, List, Mention, PasteFromOffice, PictureEditing, Table, TableToolbar, TextTransformation, ClassicEditor, ImageCaption, ImageStyle, ImageToolbar, ImageUpload, Image, Bookmark } from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAutoSitemap } from "@/hooks/useAutoSitemap";
import { useSitemapAutoUpdate } from "@/hooks/useSitemapAutoUpdate";
import { useSitemapEventHandler } from "@/utils/sitemapEventHandler";

export const AddPostPage = () => {
    const { slug } = useParams();

    const [data, setData] = useState("");
    const [title, setTitle] = useState("");
    const [slugRow, setSlugRow] = useState("");
    const [image, setImage] = useState("");
    const [minutes, setMinutes] = useState(0);
    const [status, setStatus] = useState("published");

    const editorToolbarRef = useRef(null);
    const navigate = useNavigate();
    const { updateSitemapAndPing } = useSitemapAutoUpdate();
    const sitemapEvents = useSitemapEventHandler(updateSitemapAndPing);

    useEffect(() => {
        if (slug) {
            const fetchPost = async () => {
                try {
                    const res = await posts.getPost(slug);
                    setData(res.post.content);
                    setTitle(res.post.title);
                    setSlugRow(res.post.slug);
                    setImage(res.post.image);
                    setMinutes(res.post.timeToRead);
                    setStatus(res.post.status ? "published" : "draft");
                } catch (err) {
                    console.error(err.message);
                }
            }

            fetchPost();
        }
    }, [slug])

    const editorConfig = {
        licenseKey: 'GPL', // Or 'GPL'.
        plugins: [
            Essentials,
            Autoformat,
            Bold,
            Italic,
            BlockQuote,
            CloudServices,
            Heading,
            Image,
            ImageCaption,
            ImageStyle,
            ImageToolbar,
            ImageUpload,
            Base64UploadAdapter,
            Indent,
            Link,
            Bookmark,
            List,
            Mention,
            Paragraph,
            PasteFromOffice,
            PictureEditing,
            Table,
            TableToolbar,
            TextTransformation,
        ],
        toolbar: [
            'undo',
            'redo',
            '|',
            'heading',
            '|',
            'bold',
            'italic',
            '|',
            'link',
            'bookmark',
            'uploadImage',
            'insertTable',
            'blockQuote',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'outdent',
            'indent'
        ],
        heading: {
            options: [
                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                {
                    model: 'heading1',
                    view: 'h1',
                    title: 'Heading 1',
                    class: 'ck-heading_heading1'
                },
                {
                    model: 'heading2',
                    view: 'h2',
                    title: 'Heading 2',
                    class: 'ck-heading_heading2'
                },
                {
                    model: 'heading3',
                    view: 'h3',
                    title: 'Heading 3',
                    class: 'ck-heading_heading3'
                },
                {
                    model: 'heading4',
                    view: 'h4',
                    title: 'Heading 4',
                    class: 'ck-heading_heading4'
                }
            ]
        },
        image: {
            toolbar: [
                'imageStyle:inline',
                'imageStyle:block',
                'imageStyle:side',
                '|',
                'toggleImageCaption',
                'imageTextAlternative'
            ]
        },
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://'
        },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        },
    }

    function transliterate(text) {
        const map = {
            а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "yo", ж: "zh",
            з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o",
            п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "kh", ц: "ts",
            ч: "ch", ш: "sh", щ: "shch", ы: "y", э: "e", ю: "yu", я: "ya",
            ь: "", ъ: "",
        };

        return text
            .split(/(\s+)/) // сохраняем пробелы
            .map(word => {
                // если слово содержит только латиницу — оставляем как есть
                if (/^[a-zA-Z0-9]+$/.test(word)) return word;

                return word
                    .split("")
                    .map(char => {
                        const lower = char.toLowerCase();
                        const latin = map[lower] || char;
                        return char === lower ? latin : latin.charAt(0).toUpperCase() + latin.slice(1);
                    })
                    .join("");
            })
            .join("");
    }

    const handleTitleChange = (e) => {
        const value = e.target.value;

        const _slug = transliterate(value)
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, "")     // убираем всё кроме латиницы, цифр, пробелов и дефисов
            .replace(/\s+/g, "-")              // заменяем пробелы на дефис
            .replace(/-+/g, "-")               // убираем повторные дефисы
            .replace(/^-+|-+$/g, "");          // убираем дефисы по краям

        setTitle(value);
        setSlugRow(_slug);
    };

    const handleSlugChange = (e) => {
        const value = e.target.value;
        setSlugRow(value);
    }


    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const compressedFile = await imageCompression(file, {
                maxSizeMB: 0.5,           // максимум 0.5 МБ
                maxWidthOrHeight: 800,    // ограничиваем размер
                useWebWorker: true,
            });

            const base64 = await imageCompression.getDataUrlFromFile(compressedFile);
            setImage(base64);
        } catch (err) {
            console.error("Ошибка при сжатии:", err);
        }
    };


    const handleStatusChange = (e) => {
        console.log(e.target.value)
        const value = e.target.value;
        setStatus(value);
    }

    const handleMinutesChange = (e) => {
        const value = e.target.value;
        setMinutes(value);
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            console.log('📝 Creating new article:', { title, slug: slugRow, status });
            
            const res = await posts.createPost({ 
                title, 
                slug: slugRow, 
                content: data, 
                image, 
                timeToRead: Number(minutes), 
                published: status === "published" ? true : false 
            });
            
            if (res.post._id) {
                toast.success("Статья успешно создана");
                
                // Автоматически обновляем sitemap и отправляем в поисковые системы
                if (status === "published") {
                    console.log('🔄 Auto-updating sitemap for new article:', res.post.slug);
                    await sitemapEvents.onArticleCreated({
                        title: res.post.title,
                        slug: res.post.slug
                    });
                } else {
                    console.log('📝 Article created as draft, skipping sitemap update');
                }
                
                navigate(`/article/${res.post.slug}`);
            } else {
                toast.error("Ошибка при создании статьи");
            }
            
            // Очищаем форму
            setData("");
            setTitle("");
            setSlugRow("");
            setImage("");
            setMinutes(0);
            setStatus("published");
        } catch (error) {
            console.error('❌ Error creating article:', error);
            toast.error("Ошибка при создании статьи");
        }
    }

    const handleEditComplete = async (e) => {
        e.preventDefault();

        try {
            console.log('✏️ Updating article:', { slug, title, status });
            
            const res = await posts.updatePost(slug, {
                title,
                content: data,
                image,
                minutes,
                published: status === "published" ? true : false,
            });
            
            console.log('Article update response:', res);
            
            if (res.message === "Post updated") {
                toast.success("Статья успешно обновлена");
                
                // Автоматически обновляем sitemap и отправляем в поисковые системы
                if (status === "published") {
                    console.log('🔄 Auto-updating sitemap for updated article:', slug);
                    await sitemapEvents.onArticleUpdated({
                        title: title,
                        slug: slug
                    });
                } else {
                    console.log('📝 Article updated as draft, removing from sitemap if needed');
                    // Можно добавить логику удаления из sitemap для черновиков
                    await sitemapEvents.onArticleUpdated({
                        title: title,
                        slug: slug
                    });
                }
                
                navigate(`/article/${slug}`);
                
                // Очищаем форму
                setData("");
                setTitle("");
                setSlugRow("");
                setImage("");
                setMinutes(0);
                setStatus("published");
            } else {
                toast.error("Ошибка при обновлении статьи");
            }
        } catch (error) {
            console.error('❌ Error updating article:', error);
            toast.error("Ошибка при обновлении статьи");
        }
    }

    const onBack = () => {
        navigate('/admin')
    }

    return (
        <div>
            <div className="bg-card rounded-lg border p-6 mt-4">
                <Button variant="outline" size="sm" onClick={onBack}>
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Назад
                </Button>
                <h3 className="my-4">Добавить статью</h3>
                <form className="flex flex-col gap-4" onSubmit={slug ? handleEditComplete : handleSubmit}>
                    <label className="text-sm font-medium text-foreground">Заголовок</label>
                    <input value={title} onChange={handleTitleChange} type="text" className="w-full rounded-lg border border-foreground p-2 max-w-xs" placeholder="Заголовок" />
                    <label className="text-sm font-medium text-foreground">Slug</label>
                    <input value={slugRow} onChange={handleSlugChange} type="text" className="w-full rounded-lg border border-foreground p-2 max-w-xs" placeholder="Slug" />

                    <div className="flex flex-col">
                        <label className="text-sm font-medium text-foreground">Загрузить изображение</label>
                        <input onChange={handleImageChange} type="file" className="w-full rounded-lg border border-foreground p-2 max-w-xs" placeholder="Загрузить изображение" />
                        {image && <img src={image} alt="Изображение" className="w-full rounded-lg border border-foreground p-2 max-w-xs" />}
                    </div>

                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-foreground">Статус</label>
                            <select value={status} onChange={handleStatusChange} className="rounded-lg border border-foreground p-2 max-w-xs">
                                <option value="published">Опубликовано</option>
                                <option value="draft">Черновик</option>
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-foreground">Время чтения (минут)</label>
                            <input value={minutes} onChange={handleMinutesChange} type="number" className="w-full rounded-lg border border-foreground p-2 max-w-xs" placeholder="Время чтения" />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-foreground">Текст</label>
                            <div ref={editorToolbarRef}></div>
                            <CKEditor
                                editor={ClassicEditor}
                                data={data}
                                config={editorConfig}
                                onReady={(editor) => {
                                    if (editorToolbarRef.current) {
                                        editorToolbarRef.current.appendChild(editor.ui.view.toolbar.element);
                                    }
                                }}
                                onAfterDestroy={(editor) => {
                                    if (editorToolbarRef.current) {
                                        Array.from(editorToolbarRef.current.children).forEach(child => child.remove());
                                    }
                                }}
                                onChange={((_, e) => setData(e.getData()))}
                            />
                        </div>
                        <div className="flex justify-end">
                            <button className="bg-primary text-white rounded-lg px-4 py-2 font-medium">Сохранить</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}